import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  IntegrationsV2Query,
  MetadataInput,
  PropertyFilterInputV2,
  ConnectionStatusV2
} from '@/gql/graphql';
import { INTEGRATION_V2_QUERY } from '@/queries/integration';
import { ApolloClient } from '@apollo/client';
import {
  UPDATE_INTEGRATION_EVENTS_V2_MUTATION,
  UPDATE_INTEGRATION_CONSENT_MODE_V2_MUTATION,
  UPDATE_INTEGRATION_SMART_QUALIFY_V2_MUTATION,
  UPDATE_CREDENTIALS_MUTATION,
  UPDATE_INTEGRATION_METADATA_MUTATION,
  UPDATE_INTEGRATION_PROPERTY_FILTERS_V2_MUTATION,
  DELETE_INTEGRATION_V2,
  UPDATE_INTEGRATION_STATUS_V2
} from '@/mutations/integrations';
// Extract the integration type from the query
type Integration = IntegrationsV2Query['integrationsV2'][number];
interface IntegrationUpdaterProps {
  id: string;
  customEvents: string[];
  consentMode: boolean;
  smartQualify?: boolean;
  credentials?: Record<string, any>;
}

interface IntegrationMetadataProps {
  id: string;
  metadata: MetadataInput;
}

interface IntegrationPropertyFiltersProps {
  id: string;
  input: PropertyFilterInputV2[];
}

interface IntegrationStatusProps {
  id: string;
  status: ConnectionStatusV2;
}

interface IntegrationState {
  integration: Integration | null;
  isLoading: boolean;
  error: string | null;
  isPublishable: boolean;
  isIntegrationFetchLoading: boolean;

  // Actions
  fetchIntegration: (client: ApolloClient<unknown>, id: string) => Promise<void>;
  setIsPublishable: (isPublishable: boolean) => void;
  updateIntegrationWithResponse: (responseData: Integration) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
  updateSettings: (apolloClient: ApolloClient, settings: IntegrationUpdaterProps) => Promise<void>;
  updateIntegration: (
    apolloClient: ApolloClient,
    settings: IntegrationUpdaterProps
  ) => Promise<void>;
  updateMetadata: (apolloClient: ApolloClient, metadata: IntegrationMetadataProps) => Promise<void>;
  updatePropertyFilters: (
    apolloClient: ApolloClient,
    propertyFilters: IntegrationPropertyFiltersProps
  ) => Promise<void>;
  deleteIntegration: (apolloClient: ApolloClient, id: string) => Promise<void>;
  updateIntegrationStatus: (
    apolloClient: ApolloClient,
    statusUpdate: IntegrationStatusProps
  ) => Promise<void>;
}

// Every read below is `network-only` on purpose. `client.query` defaults to
// cache-first, and the mutations that change filters (events + property filters)
// only select `id` back, so the Apollo cache keeps the pre-mutation
// `findFiltersV2`. A cache-first refetch would hand back those stale filters and
// the save would look like it never happened.
export const useIntegrationStore = create<IntegrationState>()(
  devtools(
    (set, _) => ({
      integration: null,
      isLoading: false,
      error: null,
      isPublishable: false,
      isIntegrationFetchLoading: false,
      setIsPublishable: (isPublishable) => {
        set({ isPublishable }, false, 'setIsPublishable');
      },
      fetchIntegration: async (client: ApolloClient, id: string) => {
        set({ isIntegrationFetchLoading: true, error: null });
        try {
          const {
            data: { integrationV2 }
          } = await client.query({
            query: INTEGRATION_V2_QUERY,
            variables: { id },
            fetchPolicy: 'network-only'
          });
          set(
            { integration: integrationV2, isIntegrationFetchLoading: false },
            false,
            'setIntegration'
          );
        } catch (error: unknown) {
          set(
            { error: error instanceof Error ? error.message : 'Unknown error', isLoading: false },
            false,
            'setError'
          );
        }
      },
      updateSettings: async (
        client: ApolloClient,
        { id, customEvents, consentMode, smartQualify, credentials }: IntegrationUpdaterProps
      ) => {
        set({ isLoading: true, error: null });
        try {
          if (smartQualify) {
            await client.mutate({
              mutation: UPDATE_INTEGRATION_SMART_QUALIFY_V2_MUTATION,
              variables: { id, smartQualify }
            });
          }

          await client.mutate({
            mutation: UPDATE_CREDENTIALS_MUTATION,
            variables: { id, credentials }
          });

          await client.mutate({
            mutation: UPDATE_INTEGRATION_EVENTS_V2_MUTATION,
            variables: { id, events: customEvents }
          });

          await client.mutate({
            mutation: UPDATE_INTEGRATION_CONSENT_MODE_V2_MUTATION,
            variables: { id, consentMode }
          });

          const {
            data: { integrationV2 }
          } = await client.query({
            query: INTEGRATION_V2_QUERY,
            variables: { id },
            fetchPolicy: 'network-only'
          });
          set({ integration: integrationV2, isLoading: false }, false, 'setIntegration');
        } catch (error: unknown) {
          set(
            { error: error instanceof Error ? error.message : 'Unknown error', isLoading: false },
            false,
            'setError'
          );
          throw error;
        }
      },
      updateMetadata: async (client: ApolloClient, { id, metadata }: IntegrationMetadataProps) => {
        set({ isLoading: true, error: null });
        try {
          await client.mutate({
            mutation: UPDATE_INTEGRATION_METADATA_MUTATION,
            variables: { id, metadata }
          });

          // Refetch the integration to get updated data
          const {
            data: { integrationV2 }
          } = await client.query({
            query: INTEGRATION_V2_QUERY,
            variables: { id },
            fetchPolicy: 'network-only'
          });
          set({ integration: integrationV2, isLoading: false }, false, 'setIntegration');
        } catch (error: unknown) {
          set(
            { error: error instanceof Error ? error.message : 'Unknown error', isLoading: false },
            false,
            'setError'
          );
          throw error;
        }
      },
      updatePropertyFilters: async (
        client: ApolloClient,
        { id, input }: IntegrationPropertyFiltersProps
      ) => {
        set({ isLoading: true, error: null });
        try {
          await client.mutate({
            mutation: UPDATE_INTEGRATION_PROPERTY_FILTERS_V2_MUTATION,
            variables: { id, input }
          });

          // Refetch the integration to get updated data
          const {
            data: { integrationV2 }
          } = await client.query({
            query: INTEGRATION_V2_QUERY,
            variables: { id },
            fetchPolicy: 'network-only'
          });
          set({ integration: integrationV2, isLoading: false }, false, 'setIntegration');
        } catch (error: unknown) {
          set(
            { error: error instanceof Error ? error.message : 'Unknown error', isLoading: false },
            false,
            'setError'
          );
          throw error;
        }
      },
      deleteIntegration: async (client: ApolloClient, id: string) => {
        set({ isLoading: true, error: null });
        try {
          await client.mutate({
            mutation: DELETE_INTEGRATION_V2,
            variables: { id }
          });

          // Clear the integration from store since it's deleted
          set({ integration: null, isLoading: false }, false, 'deleteIntegration');
        } catch (error: unknown) {
          set(
            { error: error instanceof Error ? error.message : 'Unknown error', isLoading: false },
            false,
            'setError'
          );
          throw error;
        }
      },
      updateIntegrationStatus: async (
        client: ApolloClient,
        { id, status }: IntegrationStatusProps
      ) => {
        set({ isLoading: true, error: null });
        try {
          await client.mutate({
            mutation: UPDATE_INTEGRATION_STATUS_V2,
            variables: { id, status }
          });

          // Refetch the integration to get updated data
          const {
            data: { integrationV2 }
          } = await client.query({
            query: INTEGRATION_V2_QUERY,
            variables: { id },
            fetchPolicy: 'network-only'
          });
          set({ integration: integrationV2, isLoading: false }, false, 'setIntegration');
        } catch (error: unknown) {
          set(
            { error: error instanceof Error ? error.message : 'Unknown error', isLoading: false },
            false,
            'setError'
          );
          throw error;
        }
      },
      updateIntegrationWithResponse: (responseData) => {
        set({ integration: responseData }, false, 'updateIntegrationWithResponse');
      },

      setLoading: (isLoading) => {
        set({ isLoading }, false, 'setLoading');
      },

      setError: (error) => {
        set({ error }, false, 'setError');
      },

      reset: () => {
        set(
          {
            integration: null,
            isLoading: false,
            error: null
          },
          false,
          'reset'
        );
      }
    }),
    { name: 'tracking-form-store' }
  )
);
